"use client";

/**
 * Shared UI for the auth pages (/signin, /signup) - fonts, design-token class
 * strings, and the common panels from the signin design handoff. Colors come
 * from the `.signin-scope` palette in globals.css (light + dark).
 */

import { Nunito_Sans, Playfair_Display } from "next/font/google";
import { AuthLanguageSelector } from "@/components/auth-language-selector";
import { useEffect, useState } from "react";
import { useTranslations } from "next-intl";
import { Moon, Sun } from "lucide-react";
import { useTheme } from "next-themes";
import Logo from "@/components/logo";
import { cn } from "@/utils";

export const nunitoSans = Nunito_Sans({
  subsets: ["latin"],
  weight: ["400", "600", "700", "800", "900"],
});

export const playfairDisplay = Playfair_Display({
  subsets: ["latin"],
  weight: ["500", "600"],
  style: ["normal", "italic"],
});

export const EMAIL_RE = /.+@.+\..+/;

export const inputClass = cn(
  "mt-2 w-full box-border rounded-xl border-[1.5px] border-[color:var(--si-border)] bg-[color:var(--si-surface)] px-4 py-3.5 text-[15px] text-[color:var(--si-ink)] outline-none transition-[border-color,box-shadow,background-color,color] duration-200",
  "placeholder:text-[color:var(--si-muted)]",
  "focus:border-[color:var(--si-ink)] focus:shadow-[0_0_0_4px_var(--si-focus-ring)]",
);

export const secondaryBtnClass = cn(
  "flex w-full items-center justify-center gap-3 rounded-xl border border-[color:var(--si-border)] bg-[color:var(--si-surface)] px-[18px] py-3.5 text-[15px] font-bold text-[color:var(--si-ink)] transition-colors duration-200",
  "hover:border-[color:var(--si-ink)] disabled:cursor-not-allowed disabled:opacity-50",
);

export const primaryBtnClass = cn(
  "mt-[22px] flex w-full items-center justify-center rounded-xl px-[18px] py-[15px] text-[15px] font-extrabold transition-colors duration-200",
  "bg-[color:var(--si-primary-bg)] text-[color:var(--si-primary-fg)] hover:bg-[color:var(--si-primary-hover)]",
  "disabled:cursor-default disabled:bg-[color:var(--si-primary-disabled)] disabled:text-[color:var(--si-primary-disabled-fg)] disabled:hover:bg-[color:var(--si-primary-disabled)]",
);

export const labelClass = "block text-sm font-extrabold text-[color:var(--si-ink)]";

export function ThemeToggleButton() {
  const { theme, setTheme } = useTheme();
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  if (!mounted) return null;

  return (
    <button
      type="button"
      onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
      className="flex h-9 w-9 items-center justify-center rounded-full border border-[color:var(--si-border)] bg-[color:var(--si-surface)] text-[color:var(--si-ink)] transition-colors duration-200 hover:border-[color:var(--si-ink)]"
      aria-label="Toggle theme"
    >
      {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
    </button>
  );
}

export function GoogleLogo() {
  return (
    <svg className="h-[19px] w-[19px]" viewBox="0 0 48 48">
      <path
        fill="#FFC107"
        d="M43.6 20.1H42V20H24v8h11.3C33.7 32.7 29.3 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3l5.7-5.7C34.3 6.1 29.4 4 24 4 13 4 4 13 4 24s9 20 20 20 20-9 20-20c0-1.3-.1-2.6-.4-3.9z"
      />
      <path
        fill="#FF3D00"
        d="m6.3 14.7 6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.9 1.2 8 3l5.7-5.7C34.3 6.1 29.4 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"
      />
      <path
        fill="#4CAF50"
        d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.3 0-9.7-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z"
      />
      <path
        fill="#1976D2"
        d="M43.6 20.1H42V20H24v8h11.3c-.8 2.2-2.2 4.1-4.1 5.5l6.2 5.2C36.9 39.2 44 34 44 24c0-1.3-.1-2.6-.4-3.9z"
      />
    </svg>
  );
}

/** Apple mark in currentColor so it follows the button's ink in both themes. */
export function AppleLogo() {
  return (
    <svg className="h-[19px] w-[19px]" viewBox="0 0 814 1000" fill="currentColor" aria-hidden="true">
      <path d="M788.1 340.9c-5.8 4.5-108.2 62.2-108.2 190.5 0 148.4 130.3 200.9 134.2 202.2-.6 3.2-20.7 71.9-68.7 141.9-42.8 61.6-87.5 123.1-155.5 123.1s-85.5-39.5-164-39.5c-76.5 0-103.7 40.8-165.9 40.8s-105.6-57-155.5-127C46.7 790.7 0 663 0 541.8c0-194.4 126.4-297.5 250.8-297.5 66.1 0 121.2 43.4 162.7 43.4 39.5 0 101.1-46 176.3-46 28.5 0 130.9 2.6 198.3 99.2zm-234-181.5c31.1-36.9 53.1-88.1 53.1-139.3 0-7.1-.6-14.3-1.9-20.1-50.6 1.9-110.8 33.7-147.1 75.8-28.5 32.4-55.1 83.6-55.1 135.5 0 7.8 1.3 15.6 1.9 18.1 3.2.6 8.4 1.3 13.6 1.3 45.4 0 102.5-30.4 135.5-71.3z" />
    </svg>
  );
}

/** Left editorial panel: logo, Playfair hero, drifting blobs, footer line. */
export function AuthLeftPanel({
  namespace,
  footerKey,
}: {
  namespace: "auth.signin" | "auth.signup";
  footerKey: "trustedBy" | "freeToStart";
}) {
  const t = useTranslations(namespace);
  return (
    <div className="relative box-border flex min-w-[320px] flex-[1_1_460px] flex-col overflow-hidden bg-[color:var(--si-panel)] px-[clamp(24px,5vw,64px)] py-[clamp(24px,4vw,48px)] transition-colors duration-300">
      {/* Ambient blobs - same treatment as the onboarding flow */}
      <div className="pointer-events-none absolute inset-0">
        <div
          data-signin-anim
          className="absolute -right-[120px] -top-[140px] h-[460px] w-[460px] rounded-full"
          style={{
            background: "radial-gradient(circle, var(--si-blob-1), transparent 68%)",
            animation: "onboarding-drift-1 22s ease-in-out infinite",
          }}
        />
        <div
          data-signin-anim
          className="absolute -bottom-[160px] -left-[140px] h-[420px] w-[420px] rounded-full"
          style={{
            background: "radial-gradient(circle, var(--si-blob-2), transparent 65%)",
            animation: "onboarding-drift-2 16s ease-in-out infinite",
          }}
        />
      </div>

      {/* Logo (theme-aware: the panel follows the theme) */}
      <div className="relative">
        <Logo className="h-8 w-auto" />
      </div>

      {/* Hero */}
      <div className="relative flex flex-1 flex-col justify-center py-12">
        <h1
          className={cn(
            playfairDisplay.className,
            "m-0 text-[clamp(44px,5.5vw,76px)] font-medium leading-[1.08] tracking-[-0.01em] text-[color:var(--si-ink)]",
          )}
        >
          {t("headline1")}
          <br />
          {t("headline2")}
          <br />
          <span className="italic text-[color:var(--si-hero-accent)]">{t("headline3")}</span>
        </h1>
        <p className="mb-0 mt-7 max-w-[440px] text-base leading-[1.65] text-[color:var(--si-body)] [text-wrap:pretty]">
          {t("description")}
        </p>
      </div>

      {/* Footer */}
      <div className="relative flex items-center gap-3.5">
        <div className="h-px w-11 bg-[color:var(--si-rule)]" />
        <span className="text-xs font-bold uppercase tracking-[2.5px] text-[color:var(--si-body-2)]">
          {t(footerKey)}
        </span>
      </div>
    </div>
  );
}

/** Terms-of-service footer line shown on every auth state. */
export function AuthTermsFooter({ namespace }: { namespace: "auth.signin" | "auth.signup" }) {
  const t = useTranslations(namespace);
  return (
    <p className="mb-0 mt-11 text-center text-[12.5px] leading-[1.6] text-[color:var(--si-muted)]">
      {t("termsText")}{" "}
      <a href="/terms" className="text-[color:var(--si-body-2)] transition-colors hover:text-[color:var(--si-ink)]">
        {t("termsOfService")}
      </a>{" "}
      {t("and")}{" "}
      <a href="/privacy" className="text-[color:var(--si-body-2)] transition-colors hover:text-[color:var(--si-ink)]">
        {t("privacyPolicy")}
      </a>
    </p>
  );
}

/** Bottom-right utilities: language pill + theme toggle. */
export function AuthUtilitiesRow() {
  return (
    <div className="flex justify-end gap-2.5 pt-5">
      <AuthLanguageSelector position="top" />
      <ThemeToggleButton />
    </div>
  );
}

/** Loading skeleton shared by both auth pages. */
export function AuthPageSkeleton() {
  return (
    <div className="signin-scope flex min-h-screen w-full flex-wrap bg-[color:var(--si-page)]">
      <div className="relative box-border hidden min-w-[320px] flex-[1_1_460px] flex-col justify-between overflow-hidden bg-[color:var(--si-panel)] px-[clamp(24px,5vw,64px)] py-[clamp(24px,4vw,48px)] lg:flex">
        <div className="h-8 w-32 animate-pulse rounded-lg bg-[color:var(--si-hairline)]" />
        <div className="space-y-4">
          <div className="h-24 w-72 animate-pulse rounded-lg bg-[color:var(--si-hairline)]" />
          <div className="h-16 w-64 animate-pulse rounded-lg bg-[color:var(--si-hairline)]" />
        </div>
        <div className="h-4 w-48 animate-pulse rounded bg-[color:var(--si-hairline)]" />
      </div>

      <div className="flex min-w-[320px] flex-[1_1_460px] flex-col items-center justify-center px-6">
        <div className="w-full max-w-[440px] space-y-8">
          <div className="space-y-2">
            <div className="h-8 w-40 animate-pulse rounded bg-[color:var(--si-hairline)]" />
            <div className="h-4 w-56 animate-pulse rounded bg-[color:var(--si-hairline)]" />
          </div>
          <div className="h-12 w-full animate-pulse rounded-xl bg-[color:var(--si-hairline)]" />
          <div className="h-12 w-full animate-pulse rounded-xl bg-[color:var(--si-hairline)]" />
        </div>
      </div>
    </div>
  );
}
